if (!customElements.get('sw-gallery')) {
  class SwGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-sw-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-sw-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-sw-dot]'));
      this.thumbList = this.querySelector('.sw-pdp__thumbs');
      this.stage = this.querySelector('.sw-pdp__stage');
      this.counter = this.querySelector('[data-sw-counter]');
      this.lightbox = this.querySelector('[data-sw-lightbox]');
      this.pairs = this.slides.map((slide, index) => ({
        slide,
        thumb: this.thumbs[index] || null,
        item: this.thumbs[index] ? this.thumbs[index].closest('li') : null,
      }));
      this.visible = this.pairs.slice();
      this.index = 0;

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
      this.stage?.addEventListener('pointerdown', this.onPointerDown);
      this.stage?.addEventListener('pointerup', this.onPointerUp);
      this.stage?.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.lightbox) this.setupLightbox();

      if (this.visible.length) {
        const current = this.visible.findIndex((pair) => pair.thumb && pair.thumb.getAttribute('aria-current') === 'true');
        this.selectVisible(current > -1 ? current : 0);
      }
    }

    disconnectedCallback() {
      this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      this.stage?.removeEventListener('pointerdown', this.onPointerDown);
      this.stage?.removeEventListener('pointerup', this.onPointerUp);
      this.stage?.removeEventListener('scroll', this.onScroll);
    }

    onThumbClick(event) {
      const index = this.visible.findIndex((pair) => pair.thumb === event.currentTarget);
      if (index >= 0) this.selectVisible(index);
    }

    onClick(event) {
      const dot = event.target.closest('[data-sw-dot]');
      if (dot) {
        const index = this.dots.indexOf(dot);
        if (index >= 0) this.selectVisible(index);
        return;
      }
      if (event.target.closest('[data-sw-gallery-prev]')) return this.step(-1);
      if (event.target.closest('[data-sw-gallery-next]')) return this.step(1);
      if (event.target.closest('[data-sw-gallery-zoom]')) return this.openLightbox();
      if (this.suppressClick) return;
      if (event.target.closest('.sw-pdp__slide img')) this.openLightbox();
    }

    onKeydown(event) {
      if (this.lightbox?.open) return;
      const from = this.visible.findIndex((pair) => pair.thumb === document.activeElement);
      if (from === -1) return;
      const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const next = (from + step + this.visible.length) % this.visible.length;
      this.visible[next].thumb.focus();
      this.selectVisible(next);
    }

    scrolling() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    onScroll() {
      if (!this.scrolling() || !this.stage) return;
      const index = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
      if (index !== this.index && index >= 0 && index < this.visible.length) {
        this.selectVisible(index, { scroll: false });
      }
    }

    onPointerDown(event) {
      if (this.scrolling()) return;
      this.pointerStart = event.pointerType === 'mouse' ? null : { x: event.clientX, y: event.clientY };
      this.suppressClick = false;
    }

    onPointerUp(event) {
      if (!this.pointerStart) return;
      const dx = event.clientX - this.pointerStart.x;
      const dy = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      this.suppressClick = true;
      window.setTimeout(() => (this.suppressClick = false), 350);
      this.step(dx < 0 ? 1 : -1);
    }

    step(direction) {
      if (this.visible.length < 2) return;
      const next = (this.index + direction + this.visible.length) % this.visible.length;
      this.selectVisible(next);
    }

    showMatching(matches, mediaId) {
      const matching = this.pairs.filter((pair) => matches(pair.slide.dataset.swAlt || ''));
      this.visible = matching.length ? matching : this.pairs.slice();
      this.pairs.forEach((pair) => {
        const on = this.visible.includes(pair);
        if (pair.item) pair.item.hidden = !on;
        if (!on) pair.slide.setAttribute('hidden', '');
      });
      const preferred = this.visible.findIndex((pair) => pair.slide.dataset.mediaId === String(mediaId));
      this.selectVisible(preferred >= 0 ? preferred : 0);
      this.setAttribute('data-sw-ready', '');
    }

    selectVisible(index, options = {}) {
      const current = this.visible[index] || this.visible[0];
      if (!current) return;
      this.index = this.visible.indexOf(current);
      const scrolling = this.scrolling();
      this.pairs.forEach((pair, pairIndex) => {
        const active = pair === current;
        if (this.visible.includes(pair) && !scrolling) pair.slide.toggleAttribute('hidden', !active);
        if (pair.thumb) pair.thumb.setAttribute('aria-current', active ? 'true' : 'false');
        const dot = this.dots[pairIndex];
        if (dot) dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (scrolling && options.scroll !== false && this.stage) {
        this.stage.scrollTo({ left: this.index * this.stage.clientWidth, behavior: 'smooth' });
      }

      [1, -1].forEach((offset) => {
        const neighbour = this.visible[(this.index + offset + this.visible.length) % this.visible.length];
        const image = neighbour?.slide.querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      });

      this.toggleAttribute('data-sw-single', this.visible.length < 2);
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.visible.length}`;
      this.revealThumb(current.item);
      if (this.lightbox?.open) this.renderLightbox();
    }

    select(index) {
      this.selectVisible(index);
    }

    revealThumb(item) {
      const list = this.thumbList;
      if (!list || !item || list.scrollWidth <= list.clientWidth) return;
      const left = item.offsetLeft - list.offsetLeft;
      const target = left - (list.clientWidth - item.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    /* ---------- Lightbox ---------- */

    setupLightbox() {
      const box = this.lightbox;
      this.lightboxImage = box.querySelector('[data-sw-lightbox-image]');
      ['srcset', 'sizes', 'width', 'height', 'loading'].forEach((name) => this.lightboxImage?.removeAttribute(name));
      this.lightboxCounter = box.querySelector('[data-sw-lightbox-counter]');
      this.lightboxFrame = box.querySelector('.sw-lightbox__frame');

      box.addEventListener('click', (event) => {
        if (event.target.closest('[data-sw-lightbox-close]') || event.target === box) return this.closeLightbox();
        if (event.target.closest('[data-sw-lightbox-prev]')) return this.step(-1);
        if (event.target.closest('[data-sw-lightbox-next]')) return this.step(1);
        if (event.target === this.lightboxImage) this.toggleZoom(event);
      });
      box.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
      box.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
        this.setZoom(false);
      });
      this.lightboxFrame?.addEventListener('pointermove', (event) => {
        if (box.classList.contains('is-zoomed')) this.panTo(event);
      });

      let touchStart = null;
      this.lightboxFrame?.addEventListener('pointerdown', (event) => {
        touchStart = event.pointerType === 'mouse' ? null : event.clientX;
      });
      this.lightboxFrame?.addEventListener('pointerup', (event) => {
        if (touchStart === null || box.classList.contains('is-zoomed')) return;
        const dx = event.clientX - touchStart;
        touchStart = null;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      });
    }

    openLightbox() {
      const box = this.lightbox;
      const current = this.visible[this.index];
      if (!box || !current || !current.slide.dataset.swFull || typeof box.showModal !== 'function') return;
      this.renderLightbox();
      box.showModal();
      document.body.classList.add('overflow-hidden');
    }

    closeLightbox() {
      if (this.lightbox?.open) this.lightbox.close();
    }

    renderLightbox() {
      const current = this.visible[this.index];
      if (!current || !this.lightboxImage) return;
      this.setZoom(false);
      const full = current.slide.dataset.swFull;
      if (full) {
        this.lightboxImage.src = full;
        this.lightboxImage.alt = current.slide.dataset.swAlt || '';
      }
      if (this.lightboxCounter) this.lightboxCounter.textContent = `${this.index + 1} / ${this.visible.length}`;
      this.lightbox.toggleAttribute('data-sw-single', this.visible.length < 2);
    }

    toggleZoom(event) {
      const zoomed = !this.lightbox.classList.contains('is-zoomed');
      this.setZoom(zoomed);
      if (zoomed) this.panTo(event);
    }

    setZoom(zoomed) {
      this.lightbox?.classList.toggle('is-zoomed', zoomed);
      if (!zoomed && this.lightboxImage) this.lightboxImage.style.transformOrigin = '';
    }

    panTo(event) {
      const rect = (this.lightboxFrame || this.lightboxImage).getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      this.lightboxImage.style.transformOrigin = `${x}% ${y}%`;
    }
  }

  customElements.define('sw-gallery', SwGallery);
}
